import React, { useCallback } from 'react';
import { defineMessages, FormattedNumber, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import {
  BodyText,
  CloudinaryImage,
  type CloudinaryTransformConfig,
  QuantityStepperV2,
  theme,
  useQuantityStepperV2,
} from '@sg/garnish';
import type { DeliveryOrderDetailInput } from '@sg/graphql-schema';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Upsell product option component.
 *
 * @note This component is memoized because the parent re-renders due to tracking open stepper state.
 */
export const BagUpsellProductOption = (props: BagUpsellProductOptionProps) => {
  const {
    product,
    size,
    addLineItemToCart,
    isAddingToCart,
    isDisabled,
    updateOpenSteppers,
    deliveryDetails,
  } = props;
  const { name, imageUrl, cost, channelCost } = product;

  const { formatMessage } = useIntl();

  const handleAddUpsell = useCallback(
    (quantity: number) => {
      addLineItemToCart({ quantity, product, deliveryDetails });
    },
    [addLineItemToCart, product, deliveryDetails],
  );

  const onShowStepper = useCallback(() => {
    updateOpenSteppers(product.id, true);
  }, [product.id, updateOpenSteppers]);

  const onHideStepper = useCallback(() => {
    updateOpenSteppers(product.id, false);
  }, [product.id, updateOpenSteppers]);

  const { quantity, isStepperShown, increaseQuantity, decreaseQuantity, showStepper } =
    useQuantityStepperV2({
      onChange: handleAddUpsell,
      shouldResetOnTimeout: true,
      onShowStepper,
      onHideStepper,
    });

  return (
    <View style={containerStyles[size]}>
      <View style={styles.imageContainer}>
        <CloudinaryImage
          baseUrl={imageUrl}
          config={CLOUDINARY_IMG_CONFIG[size]}
          contentFit="contain"
          style={styles.image}
          aria-label={name}
        />

        <View style={styles.stepperContainer}>
          <QuantityStepperV2.Container>
            <QuantityStepperV2.ToggleButton
              accessibilityLabel={formatMessage(messages.addToBagA11yLabel, { product_name: name })} // prettier-ignore
              isLoading={isAddingToCart}
              isDisabled={isDisabled}
              onPress={showStepper}
              size="xs"
            />
            <QuantityStepperV2.FloatingContainer isShown={isStepperShown} size="sm">
              <QuantityStepperV2.DecreaseButton
                onPress={decreaseQuantity}
                accessibilityLabel={formatMessage(messages.decreaseQuantity, {
                  product_name: name,
                })} // prettier-ignore
                palette="transparent"
                size="xs"
              />

              {isAddingToCart ? null : <QuantityStepperV2.Quantity quantity={quantity} size="sm" />}

              <QuantityStepperV2.IncreaseButton
                onPress={increaseQuantity}
                accessibilityLabel={formatMessage(messages.increaseQuantity, {
                  product_name: name,
                })} // prettier-ignore
                palette="transparent"
                size="xs"
              />
            </QuantityStepperV2.FloatingContainer>
          </QuantityStepperV2.Container>
        </View>
      </View>

      <View style={styles.textContainer}>
        <BodyText sizeMatch={['14']} numberOfLines={size === 'sm' ? 3 : 2}>
          {name}
        </BodyText>

        <BodyText sizeMatch={['14']}>
          <FormattedNumber value={(channelCost ?? cost) / 100} style="currency" currency="USD" />
        </BodyText>
      </View>
    </View>
  );
};

// ─── Constants ──────────────────────────────────────────────────────────────

const CARD_WIDTH = {
  sm: 90,
  md: 100,
} as const;

const CLOUDINARY_IMG_CONFIG: Record<Size, CloudinaryTransformConfig> = {
  sm: [{ crop: 'crop', width: 0.4, height: 0.56 }, { width: CARD_WIDTH.md }], // NOTE: On purpose to reduce calls to cloudinary
  md: [{ crop: 'crop', width: 0.4, height: 0.56 }, { width: CARD_WIDTH.md }],
};

// ─────────────────────────────────────────────────────────────────────────────

const messages = defineMessages({
  addToBagA11yLabel: {
    defaultMessage: 'Add {product_name} to bag',
    description: 'Upsell Rail | Product | Add to bag',
  },
  increaseQuantity: {
    defaultMessage: 'Increase quantity of "{product_name}"',
    description: 'Upsell Rail | Product | Increase quantity of',
  },
  decreaseQuantity: {
    defaultMessage: 'Decrease quantity of "{product_name}"',
    description: 'Upsell Rail | Product | Decrease quantity of',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const containerStyles = StyleSheet.create({
  sm: {
    gap: theme.spacing['2'],
    width: CARD_WIDTH.sm,
  },
  md: {
    gap: theme.spacing['2'],
    width: CARD_WIDTH.md,
  },
});

const styles = StyleSheet.create({
  imageContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    minHeight: 80,
    padding: theme.spacing['4'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
    borderRadius: theme.radius.large,
  },
  image: {
    width: '100%',
    aspectRatio: 1,
  },
  textContainer: {
    gap: theme.spacing['2'],
  },
  stepperContainer: {
    position: 'absolute',
    bottom: theme.spacing['1'],
    right: theme.spacing['1'],
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type BagUpsellProductOptionProps = {
  product: ProductUpsell;
  size: Size;
  addLineItemToCart: (params: AddLineItemToCartParams) => void;
  isAddingToCart: boolean;
  isDisabled: boolean;
  updateOpenSteppers: (productId: string, isStepperShown: boolean) => void;
  deliveryDetails?: DeliveryOrderDetailInput;
};

type Size = 'sm' | 'md';

type ProductUpsell = {
  id: string;
  name: string;
  cost: number;
  channelCost?: number;
  imageUrl: string;
};

type AddLineItemToCartParams = {
  quantity: number;
  product: ProductUpsell;
  deliveryDetails?: DeliveryOrderDetailInput;
};
